/**
 * API wrapper for the Reklamationen module (Phase 5 Stufe I, Slice 4,
 * 2026-05-03). Reklamationen sind ein Auftrags-Typ — Backend nutzt die
 * geteilte `auftraege_auftrag`-Tabelle mit typ='reklamation' und eine
 * Subset-State-Machine (entwurf -> bestaetigt -> in_arbeit ->
 * abgeschlossen). Wir importieren den `Auftrag`-Typ direkt aus
 * `auftraege-api.ts` statt zu duplizieren.
 */
import { api } from './api';
import type { Auftrag } from './auftraege-api';

export type MangelKategorie = 'material' | 'leistung' | 'terminversaeumnis' | 'sonstiges';

export const MANGEL_KATEGORIEN: readonly MangelKategorie[] = [
  'material',
  'leistung',
  'terminversaeumnis',
  'sonstiges',
];

export const MANGEL_KATEGORIE_LABEL: Record<MangelKategorie, string> = {
  material: 'Material',
  leistung: 'Leistung',
  terminversaeumnis: 'Terminversäumnis',
  sonstiges: 'Sonstiges',
};

interface MeldenReklamationInput {
  kundeId: string;
  objektId?: string;
  subunternehmerId?: string;
  titel: string;
  beschreibung?: string;
  mangelKategorie: MangelKategorie;
  /** ISO-Datum (YYYY-MM-DD). Backend coerce'd via `z.coerce.date()`. */
  gewaehrleistungBis?: string;
  projektId?: string;
}

export const reklamationenApi = {
  async list(): Promise<{ items: Auftrag[] }> {
    return api<{ items: Auftrag[] }>('/api/v1/reklamationen');
  },

  /**
   * Reklamationen-Detail wird über den allgemeinen Auftrags-Endpoint
   * gelesen, weil Reklamationen `typ='reklamation'`-Aufträge sind und
   * der Service kein eigenes `getById` exponiert. Permissions auf
   * `/api/v1/auftraege/:id` greifen analog (admin/buero/aussendienst-
   * scoped).
   */
  async getById(id: string): Promise<{ item: Auftrag }> {
    return api<{ item: Auftrag }>(`/api/v1/auftraege/${id}`);
  },

  async melden(input: MeldenReklamationInput): Promise<{ item: Auftrag }> {
    return api<{ item: Auftrag }>('/api/v1/reklamationen', { method: 'POST', body: input });
  },

  async zuordnenSub(id: string, subunternehmerId: string): Promise<{ item: Auftrag }> {
    return api<{ item: Auftrag }>(`/api/v1/reklamationen/${id}/sub`, {
      method: 'PATCH',
      body: { subunternehmerId },
    });
  },

  async starten(id: string): Promise<{ item: Auftrag }> {
    return api<{ item: Auftrag }>(`/api/v1/reklamationen/${id}/starten`, {
      method: 'POST',
      body: {},
    });
  },

  async abschliessen(id: string, notiz?: string): Promise<{ item: Auftrag }> {
    return api<{ item: Auftrag }>(`/api/v1/reklamationen/${id}/abschliessen`, {
      method: 'POST',
      body: notiz ? { notiz } : {},
    });
  },
};

/**
 * SLA-Schwellen analog zum Backend `sla-cron-service` — der Cron
 * generiert Warnungen bei 30 / 7 Tagen Restlaufzeit der
 * Gewährleistungsfrist. UI-Banner spiegelt dieselbe Stufung wider, damit
 * Außendienst und Büro dieselbe Frist-Lesart haben.
 */
type SlaLevel = 'rot' | 'orange' | 'keiner';

interface SlaInfo {
  level: SlaLevel;
  daysLeft: number;
}

export function computeSlaInfo(
  gewaehrleistungBis: string | null,
  today: Date = new Date(),
): SlaInfo | null {
  if (!gewaehrleistungBis) return null;
  const ms = Date.parse(gewaehrleistungBis);
  if (Number.isNaN(ms)) return null;
  const dayMs = 86_400_000;
  const todayUtcMidnight = Date.UTC(
    today.getUTCFullYear(),
    today.getUTCMonth(),
    today.getUTCDate(),
  );
  const targetUtcMidnight = Date.UTC(
    new Date(ms).getUTCFullYear(),
    new Date(ms).getUTCMonth(),
    new Date(ms).getUTCDate(),
  );
  const daysLeft = Math.ceil((targetUtcMidnight - todayUtcMidnight) / dayMs);
  if (daysLeft <= 7) return { level: 'rot', daysLeft };
  if (daysLeft <= 30) return { level: 'orange', daysLeft };
  return { level: 'keiner', daysLeft };
}

export const SLA_BANNER_STYLE: Record<Exclude<SlaLevel, 'keiner'>, string> = {
  rot: 'bg-rose-50 border-rose-300 text-rose-900',
  orange: 'bg-amber-50 border-amber-300 text-amber-900',
};
